import useUpdateCustomFieldValuesForAccount from '@/api/mutations/account/useUpdateCustomFieldValuesForAccount';
import useCreateUpload from '@/api/mutations/file/useCreateUpload';
import useUpdateCustomFieldValuesForProject from '@/api/mutations/project/useUpdateCustomFieldValuesForProject';
import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import {
  Button,
  Card,
  Field,
  Form,
  ISidebarCoreProps,
  Sidebar,
} from '@payaca/ambrosia';

import {
  AnyCustomFieldDefinition,
  isFieldsetDefinition,
} from '@payaca/custom-fields';
import { useCustomFieldForm } from '@payaca/custom-fields-form';
import { FieldInputSwitch } from '@payaca/custom-fields-react';
import { CustomFieldGroupRole } from '@payaca/types/custom-fields';
import { isNotNullish } from '@payaca/utilities/guards';
import { useQueryClient } from '@tanstack/react-query';
import {
  FC,
  Fragment,
  useCallback,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { Controller } from 'react-hook-form';
import useUpdateCustomFieldValuesForCustomer from '../../../api/mutations/customers/useUpdateCustomFieldValuesForCustomer';
import customerKeys from '../../../api/queries/customers/keyFactory';
import { CustomFieldGroupProps } from './CustomFieldGroup';
import { CustomFieldValueAuditLogsModal } from './CustomFieldValueAuditLogsModal';

type ModalState = {
  activeModal: 'viewCustomFieldAuditLogs' | null;
};

type ModalAction =
  | {
      type: 'openViewCustomFieldAuditLogs';
    }
  | {
      type: 'closeModal';
    };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'openViewCustomFieldAuditLogs': {
      return {
        activeModal: 'viewCustomFieldAuditLogs',
      };
    }
    case 'closeModal':
      return { activeModal: null };
    default:
      return state;
  }
};

const EditCustomFieldGroupSidebarContent: FC<{
  fields: AnyCustomFieldDefinition[];
  initialValue?: Record<string, any>;
  onSubmit: (values: any) => void;
  showAuditLogs?: () => void;
  projectId?: string;
}> = (props) => {
  const {
    fields: fieldDefinitions,
    initialValue,
    onSubmit,
    showAuditLogs,
    projectId,
  } = props;

  /**
   * queries
   */
  const { project } = useGetProjectByIdOverview(projectId);
  const { data: queryUsers } = useGetMyAccountUsersExcludedDeactivated();

  const users = useMemo(
    () =>
      queryUsers?.map((u) => ({
        ...u,
        id: u.publicId,
      })) || [],
    [queryUsers]
  );

  /**
   * mutations
   */
  const { mutateAsync: createUpload } = useCreateUpload();

  const handlePersistUpload = useCallback(
    (files: File[]) =>
      Promise.all(
        files.map(async (file) => {
          const upload = await createUpload({
            file,
            linkedEntities: project?.id
              ? [
                  {
                    entityType: 'deal',
                    entityId: project.id,
                  },
                ]
              : [],
          });

          return {
            ...upload,
            thumbnailUrl: upload.thumbnailUrl || undefined,
          };
        })
      ),
    [project?.id, createUpload]
  );

  const formMethods = useCustomFieldForm({
    definitions: fieldDefinitions,
    defaultValues: initialValue,
  });

  const renderField = (
    def: AnyCustomFieldDefinition,
    prefix: string,
    depth = 0
  ): ReactNode => {
    const name = prefix + def.identifier;

    if (isFieldsetDefinition(def)) {
      // Top-level fieldsets render children directly (the sidebar title
      // already identifies the fieldset). Nested fieldsets get a heading + card.
      if (depth === 0) {
        return (
          <Fragment key={name}>
            {def.description && <p>{def.description}</p>}
            {def.children.map((child) =>
              renderField(child, name + '.', depth + 1)
            )}
          </Fragment>
        );
      }

      return (
        <div key={name} className="space-y-2">
          <h4>{def.label}</h4>
          <Card sizeVariant="sm">
            <Card.Body className="space-y-4">
              {def.description && <p>{def.description}</p>}
              {def.children.map((child) =>
                renderField(child, name + '.', depth + 1)
              )}
            </Card.Body>
          </Card>
        </div>
      );
    }

    return (
      <Field key={name} name={name}>
        {def.description ? (
          <>
            <Field.Label>{def.label}</Field.Label>
            <div className="flex justify-between items-end">
              <Field.HelperText>{def.description}</Field.HelperText>
              {!def.isRequired && <Field.ClearButton clearValue={null} />}
            </div>
          </>
        ) : (
          <div className="flex justify-between items-end">
            <Field.Label>{def.label}</Field.Label>
            {!def.isRequired && <Field.ClearButton clearValue={null} />}
          </div>
        )}
        <Controller
          name={name}
          render={({ field: { value, onChange } }) => {
            return (
              <FieldInputSwitch
                definition={def}
                value={value}
                onChange={onChange}
                users={users}
                handlePersistUpload={handlePersistUpload}
              />
            );
          }}
        />
      </Field>
    );
  };

  return (
    <Form
      methods={formMethods}
      onSubmit={onSubmit}
      onError={console.error}
      className="h-full"
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="grow shrink overflow-y-scroll p-4 space-y-4">
          {fieldDefinitions.map((def) => renderField(def, ''))}
        </div>

        <div className="mt-auto p-4 space-y-4">
          {showAuditLogs && (
            <Button variant="link" onClick={showAuditLogs}>
              View audit log
            </Button>
          )}
          <Button
            className="w-full"
            type="submit"
            disabled={!formMethods.formState.isDirty}
            isProcessing={formMethods.formState.isSubmitting}
          >
            Save
          </Button>
        </div>
      </div>
    </Form>
  );
};

const getAuditLogTarget = (
  props: CustomFieldGroupProps
): Exclude<CustomFieldGroupProps, { type: 'account' }> | null => {
  switch (props.type) {
    case 'project':
      return { type: props.type, projectId: props.projectId };
    case 'customer':
      return { type: props.type, customerId: props.customerId };
    default:
      return null;
  }
};

const EditCustomFieldsSidebar: FC<
  Omit<ISidebarCoreProps, 'title'> & {
    type: CustomFieldGroupRole;
    fields: AnyCustomFieldDefinition[];
    initialValue?: Record<string, any>;
    fieldsetIdentifier?: string;
    fieldsetLabel?: string;
  } & CustomFieldGroupProps
> = (props) => {
  const {
    fields,
    initialValue,
    fieldsetIdentifier,
    onClose,
    type,
    fieldsetLabel,
    ...rest
  } = props;

  const [modalState, modalDispatch] = useReducer(modalReducer, {
    activeModal: null,
  });

  const auditLogTarget = getAuditLogTarget(props);

  /**
   * mutations
   */
  const queryClient = useQueryClient();
  const { mutateAsync: updateProjectCustomFieldValuesMutation } =
    useUpdateCustomFieldValuesForProject({
      projectId: type === 'project' ? props.projectId : undefined,
    });
  const { mutateAsync: updateCustomerCustomFieldValuesMutation } =
    useUpdateCustomFieldValuesForCustomer();
  const { mutateAsync: updateAccountCustomFieldValuesMutation } =
    useUpdateCustomFieldValuesForAccount();

  /**
   * callbacks
   */
  const onSubmit = async (values: any) => {
    if (!fields) {
      return;
    }

    const payload = {
      fieldValues: Object.entries(values).map(([identifier, value]) => ({
        identifier,
        value: isNotNullish(value) ? JSON.stringify(value) : null,
      })),
    };
    if (type === 'project') {
      await updateProjectCustomFieldValuesMutation({
        ...payload,
        projectId: props.projectId,
      });
    } else if (type === 'account') {
      await updateAccountCustomFieldValuesMutation(payload);
    } else {
      await updateCustomerCustomFieldValuesMutation({
        ...payload,
        customerId: props.customerId,
      });

      await queryClient.invalidateQueries({
        queryKey: customerKeys.customFields(props.customerId),
      });
    }

    onClose?.();
  };

  return (
    <>
      <Sidebar
        title={`Edit${fieldsetLabel ? ` ${fieldsetLabel}` : ''}`}
        onClose={onClose}
        {...rest}
      >
        <Sidebar.Body>
          <EditCustomFieldGroupSidebarContent
            projectId={type === 'project' ? props.projectId : undefined}
            fields={fields}
            initialValue={initialValue}
            onSubmit={onSubmit}
            showAuditLogs={
              fieldsetIdentifier && auditLogTarget
                ? () => {
                    modalDispatch({
                      type: 'openViewCustomFieldAuditLogs',
                    });
                  }
                : undefined
            }
          />
          {fieldsetIdentifier && auditLogTarget && (
            <CustomFieldValueAuditLogsModal
              isOpen={modalState.activeModal === 'viewCustomFieldAuditLogs'}
              onClose={() => modalDispatch({ type: 'closeModal' })}
              fieldIdentifier={fieldsetIdentifier}
              {...auditLogTarget}
            />
          )}
        </Sidebar.Body>
      </Sidebar>
    </>
  );
};

export default EditCustomFieldsSidebar;
